import { useDebounce } from '@ais-ui/custom-hooks';
import {
  AutoCompleteShipInfo,
  autocompleteShips,
  useSession,
} from '@ais-ui/websockets';
import { useShipApi } from '@ais-ui/api';
import { useQueries } from '@tanstack/react-query';
import type { ShipSearchQuerystringGet200ResponseRecordsInner } from '@ais-ui/api-spec/portserver';
import { isNonEmpty } from '@ais-ui/utils';
import { useEffect, useState } from 'react';

export interface AisSearchResult extends AutoCompleteShipInfo {
  requestType: 'ais' | 'no-recent-ais';
}

export interface DatabaseSearchResult
  extends ShipSearchQuerystringGet200ResponseRecordsInner {
  requestType: 'database';
  filteredOut: boolean;
}

export type ShipSearchResult = AisSearchResult | DatabaseSearchResult;

interface UseShipSearchResult {
  data: ShipSearchResult[];
  isLoading: boolean;
  isError: boolean;
  error: unknown | null;
}

const capitalizeName = (name: string | null | undefined) => {
  if (!name) return '';

  return name
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const convertToInteger = (value: string | number | null | undefined) => {
  if (!isNonEmpty(value)) return null;
  return parseInt(String(value).replace(/^0+/, ''), 10);
};

export const useShipSearch = (searchString: string): UseShipSearchResult => {
  const bouncedSearchString = useDebounce(searchString, 200);
  const { olSession } = useSession();
  const shipApi = useShipApi();
  const [isLoading, setIsLoading] = useState(false);

  const queries = useQueries({
    queries: [
      {
        queryKey: ['autocompleteShips', bouncedSearchString],
        queryFn: () => {
          if (bouncedSearchString.length < 3) {
            return Promise.resolve([]);
          }
          return autocompleteShips(olSession, bouncedSearchString);
        },
        enabled: bouncedSearchString.length > 2,
      },
      {
        queryKey: ['portServerShipSearch', bouncedSearchString],
        queryFn: () => {
          if (bouncedSearchString.length < 3) {
            return Promise.resolve([]);
          }
          return Promise.race([
            shipApi.shipSearchQuerystringGet({
              querystring: bouncedSearchString,
            }),
            new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error('Database search timeout')),
                10000
              )
            ),
          ]);
        },
        enabled: bouncedSearchString.length > 2,
      },
    ],
  });

  const [aisSearchResult, databaseSearchResult] = queries;

  // Manually control isLoading based on the enabled flag and queries' loading states
  useEffect(() => {
    // Update isLoading based on the conditions
    if (bouncedSearchString.length > 2) {
      setIsLoading(aisSearchResult.isLoading || databaseSearchResult.isLoading);
    } else {
      setIsLoading(false);
    }
  }, [
    bouncedSearchString,
    aisSearchResult.isLoading,
    databaseSearchResult.isLoading,
  ]);

  const data: ShipSearchResult[] = [
    ...(aisSearchResult?.data?.map((item: AutoCompleteShipInfo) => ({
      ...item,
      name: capitalizeName(item.name),
      imo: convertToInteger(item.imo),
      mmsi: convertToInteger(item.mmsi),
      requestType: item.timedOut ? 'no-recent-ais' : ('ais' as const),
      filteredOut: item.filteredOut || false,
    })) || []),
    ...((databaseSearchResult?.data as any)?.records?.map(
      (item: ShipSearchQuerystringGet200ResponseRecordsInner) => ({
        ...item,
        name: capitalizeName(item.name),
        requestType: 'database' as const,
        filteredOut: false,
      })
    ) || []),
  ];

  return {
    data,
    isLoading,
    isError: aisSearchResult.isError || databaseSearchResult.isError,
    error: aisSearchResult.error || databaseSearchResult.error,
  };
};
